/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { uniqBy } from 'lodash'
import { IRootState } from 'store'
import { CompareBy, ComparisonColumn, IFeatureContents, IPolicy } from '../../types'
import { fetchPolicies } from './api'
import { IComparisonState } from './state'
import {
  placeImportedPolicyColumns,
  resetComparison,
  selectProductClassCover,
  setCompareBy,
  setImportedPdfData,
} from './slice'
import {
  ensureProductClassesAsync,
  fetchFeaturesAsync,
  fetchPoliciesAsync,
  fetchPolicyContentByIdAsync,
} from './thunks'

/**
 * How an imported PDF policy has to be merged into the current comparison. The decision
 * runs on covers, because covers are what the table is actually built from:
 * - `append`: every cover the policy is written against is already loaded, so it just
 *   becomes another column.
 * - `extend-covers`: same class but at least one cover is not selected yet, so the
 *   cover selection is widened (which loads that cover's features) and the policy appended.
 * - `reset`: it needs covers outside the current class, so the comparison is cleared first.
 *   This is the only destructive outcome and the only one that prompts the user.
 */
export type ImportedPolicyPlacementMode = 'reset' | 'extend-covers' | 'append'

export interface ImportedPolicyPlacement {
  mode: ImportedPolicyPlacementMode
  classId?: number | string
  className?: string
  /**
   * Covers to select. Always covers the catalogue still offers — never a retired id, and
   * never one from outside the resolved class. Empty means the wording named no cover
   * that can still be compared.
   */
  coverIds: number[]
  coverTypes: string[]
  /**
   * The imported wordings this placement can actually show, and those it cannot. One PDF
   * can be published by several insurers, and nothing says their policies all sit in the
   * same class — the comparison holds one class at a time, so any policy outside the class
   * resolved here is left out and named back to the caller rather than dropped silently.
   */
  policies: IPolicy[]
  excludedPolicies: IPolicy[]
}

const toCoverTypeList = (coverType?: string | string[]): string[] => {
  if (Array.isArray(coverType)) return coverType
  return coverType ? [coverType] : []
}

const policyCoverIdsOf = (policy: IPolicy): number[] =>
  Array.from(new Set((policy?.sections ?? []).map((s) => s.cover_id).filter(Boolean)))

/**
 * Splits the imported wordings by whether the resolved cover selection can show them.
 *
 * A wording that names no cover the catalogue still offers is kept: its ids cannot decide
 * anything, and an import has always gone in against whatever is loaded in that case. So a
 * lone policy is never excluded here — only a policy losing the class vote to its siblings.
 */
const splitPlaceablePolicies = (
  state: IComparisonState,
  matches: IPolicy[],
  coverIds: number[],
): Pick<ImportedPolicyPlacement, 'policies' | 'excludedPolicies'> => {
  const placed = new Set(coverIds)
  const known = new Set(
    (state.productClasses ?? [])
      .flatMap((cl) => cl.classes ?? [])
      .flatMap((cls) => cls.covers ?? [])
      .map((c) => c?.id)
      .filter(Boolean),
  )

  const policies: IPolicy[] = []
  const excludedPolicies: IPolicy[] = []

  matches.forEach((policy) => {
    const recognised = policyCoverIdsOf(policy).filter((id) => known.has(id))
    const fits = !recognised.length || recognised.some((id) => placed.has(id))
    ;(fits ? policies : excludedPolicies).push(policy)
  })

  return { policies, excludedPolicies }
}

/**
 * The imported wordings' covers, cut down to the ones the catalogue still offers.
 *
 * An imported wording can name covers that no longer exist — an expired policy, or an
 * older wording written before a class was renumbered. Those ids resolve to nothing: put
 * in a cover selection they load no features, and their coverType comes out blank. Keep
 * only covers the catalogue still offers (plus whatever is already selected, in case the
 * class list on hand is partial). With no catalogue at all there is nothing to check
 * against, so the response is taken at face value.
 */
const importedCovers = (state: IComparisonState, matches: IPolicy[]) => {
  const responseCoverIds = Array.from(new Set(matches.flatMap(policyCoverIdsOf)))

  const allClasses = (state.productClasses ?? []).flatMap((cl) => cl.classes ?? [])
  const allCovers = allClasses.flatMap((cls) => cls.covers ?? [])
  const currentCoverIds = state.selectedProductClassCover?.coverId ?? []

  const availableCoverIds = new Set<number>([
    ...allCovers.map((c) => c?.id).filter(Boolean),
    ...currentCoverIds,
  ])
  const policyCoverIds = availableCoverIds.size
    ? responseCoverIds.filter((id) => availableCoverIds.has(id))
    : responseCoverIds

  return { allClasses, allCovers, policyCoverIds }
}

type CatalogueClass = ReturnType<typeof importedCovers>['allClasses'][number]

/**
 * Every class the wording has a cover in, best first.
 *
 * A combined wording — PI and Public Liability in one booklet — is written against covers
 * in more than one class, and the comparison holds one class at a time. The class the user
 * is already in comes first: they dropped the wording into that comparison, and taking the
 * first class the catalogue happened to list instead turned an ordinary add into "your
 * comparison will be cleared". After that, the class holding more of the wording's covers,
 * then catalogue order so the result is stable.
 */
const rankClasses = (
  classes: CatalogueClass[],
  coverIds: number[],
  currentClassId?: number | string,
): CatalogueClass[] => {
  const isCurrent = (cls: CatalogueClass) => currentClassId != null && cls.id === currentClassId
  return classes
    .map((cls, order) => ({
      cls,
      order,
      matched: (cls.covers ?? []).filter((c) => coverIds.includes(c?.id)).length,
    }))
    .filter(({ matched }) => matched > 0)
    .sort(
      (a, b) =>
        Number(isCurrent(b.cls)) - Number(isCurrent(a.cls)) ||
        b.matched - a.matched ||
        a.order - b.order,
    )
    .map(({ cls }) => cls)
}

export interface ImportedClassOption {
  id: number | string
  name: string
  /** The class the comparison is in now — choosing it adds rather than replaces. */
  isCurrent: boolean
}

/**
 * The classes the user could compare an imported wording in, the default first. More than
 * one means the wording spans classes and the choice is the user's to make; see rankClasses.
 */
export const importedClassOptions = (
  state: IComparisonState,
  matches: IPolicy[],
): ImportedClassOption[] => {
  const { allClasses, policyCoverIds } = importedCovers(state, matches)
  const currentClassId = state.selectedProductClassCover?.classId
  return rankClasses(allClasses, policyCoverIds, currentClassId).map((cls) => ({
    id: cls.id,
    name: cls.name,
    isCurrent: currentClassId != null && cls.id === currentClassId,
  }))
}

/**
 * `classId` is the class the user chose for a wording that spans several. Left out — or
 * naming a class the wording has no cover in — the best-ranked class is used.
 */
export const resolveImportedPolicyPlacement = (
  state: IComparisonState,
  matches: IPolicy[],
  classId?: number | string,
): ImportedPolicyPlacement => {
  const placement = resolveCoverPlacement(state, matches, classId)
  return { ...placement, ...splitPlaceablePolicies(state, matches, placement.coverIds) }
}

/**
 * Where the imported wordings' covers put the comparison. Decided on the union of every
 * matched policy's covers: they are one upload and belong on one screen, so the selection
 * has to be wide enough for all of them before any is judged out of class.
 */
const resolveCoverPlacement = (
  state: IComparisonState,
  matches: IPolicy[],
  classId?: number | string,
): Omit<ImportedPolicyPlacement, 'policies' | 'excludedPolicies'> => {
  const { allClasses, allCovers, policyCoverIds } = importedCovers(state, matches)
  const coverName = (id: number) => allCovers.find((c) => c?.id === id)?.name ?? ''

  const current = state.selectedProductClassCover
  const currentCoverIds = current?.coverId ?? []

  // Match on any of the covers, not just the first: section order is the API's choice, and
  // one unrecognised cover must not decide the class for the whole import.
  const ranked = rankClasses(allClasses, policyCoverIds, current?.classId)
  const importedClass =
    (classId != null ? ranked.find((cls) => cls.id === classId) : undefined) ?? ranked[0]

  const isSameClass =
    current?.classId != null && importedClass != null && current.classId === importedClass.id

  // Without usable covers on the imported policy there is nothing to switch to, so never
  // trade a working comparison for an empty cover selection.
  if (!policyCoverIds.length && currentCoverIds.length) {
    return {
      mode: 'append',
      classId: current?.classId,
      className: current?.className,
      coverIds: currentCoverIds,
      coverTypes: toCoverTypeList(current?.coverType),
    }
  }

  const missingCoverIds = policyCoverIds.filter((id) => !currentCoverIds.includes(id))

  /**
   * A cover selection is read back through one class: the picker's options are that
   * class's covers, and antd renders a value with no matching option as its bare id. So a
   * cover from outside the class the comparison sits in shows up in the breadcrumb as a
   * raw number, and cannot be deselected. Only covers of the resolved class can be added.
   */
  const sameClassCoverIds = isSameClass ? (importedClass?.covers ?? []).map((c) => c?.id) : []
  const addedCoverIds = missingCoverIds.filter((id) => sameClassCoverIds.includes(id))

  // Covers decide this, not the class lookup. When every cover the policy is written
  // against is already on screen, the wording belongs in the comparison the user is
  // looking at — append it even if productClasses cannot name the class. Deciding on
  // the class first meant a stale or partial class list turned an ordinary "add this
  // wording" into a destructive "replace your whole comparison?" prompt.
  if (currentCoverIds.length && !missingCoverIds.length) {
    return {
      mode: 'append',
      classId: current?.classId,
      className: current?.className,
      coverIds: currentCoverIds,
      coverTypes: toCoverTypeList(current?.coverType),
    }
  }

  // Extra covers are only safe to bolt onto a comparison of the same class; anything
  // else (unknown class, different class, nothing loaded yet) starts over.
  if (!isSameClass || !currentCoverIds.length) {
    // A fresh selection has to sit inside one class, so keep only that class's covers — a
    // stray cover from elsewhere would not belong to the class the comparison is being set
    // to. With no class resolved there is no picker that could name any of these ids, so
    // nothing is selected and the caller refuses the import rather than showing a table
    // headed by raw numbers.
    const classCoverIds = importedClass ? (importedClass.covers ?? []).map((c) => c?.id) : null
    const resetCoverIds = classCoverIds
      ? policyCoverIds.filter((id) => classCoverIds.includes(id))
      : []

    return {
      mode: 'reset',
      classId: importedClass?.id,
      className: importedClass?.name,
      coverIds: resetCoverIds,
      coverTypes: resetCoverIds.map(coverName),
    }
  }

  const currentCoverTypes = toCoverTypeList(current.coverType)

  // Every cover the wording still needs sits outside this class, so there is nothing to
  // widen the selection with. Append against what is loaded rather than dispatching a
  // cover selection identical to the current one.
  if (!addedCoverIds.length) {
    return {
      mode: 'append',
      classId: current.classId,
      className: current.className,
      coverIds: currentCoverIds,
      coverTypes: currentCoverTypes,
    }
  }

  return {
    mode: 'extend-covers',
    classId: current.classId,
    className: current.className,
    coverIds: [...currentCoverIds, ...addedCoverIds],
    coverTypes: [
      ...currentCoverIds.map((id, index) => currentCoverTypes[index] || coverName(id)),
      ...addedCoverIds.map(coverName),
    ],
  }
}

export interface ImportedPolicyLoadResult {
  mode: ImportedPolicyPlacementMode
  /** Whether anything reached the table at all — a new column or a refreshed one. */
  added: boolean
  /** Wordings that got a column of their own. */
  addedNames: string[]
  /**
   * Wordings that already had a column, so they were refreshed in place instead of a
   * second column appearing. Worth telling the user about: the table looks unchanged.
   */
  refreshedNames: string[]
  /** Wordings left out because the comparison has no column free for them. */
  skippedForLimit: string[]
  /** Wordings left out because they sit outside the class the import resolved to. */
  skippedOutOfClass: string[]
  /** Every wording in the import already had a column. */
  alreadySelected: boolean
  /** Why nothing was added, when `added` is false. */
  reason?: 'column-limit' | 'no-available-covers' | 'out-of-class' | 'no-policies'
}

/**
 * Swaps each imported policy for the one the picker lists for the same wording.
 *
 * Policies that share a wording share a proxy_id, but each has its own id, and findings are
 * fetched by id — against the features of the covers that policy is filed under. The import
 * can name a sibling the picker never shows for these covers (68616 where the picker has
 * 68613), and that sibling's findings name features that are not rows in this table, so its
 * column comes up empty. Fetching through the listed policy is what the picker and deep
 * links already do. A policy with no listed counterpart is kept as it came. Two siblings
 * that resolve to the same listed policy are one wording, so they become one column.
 *
 * Feature comparison does not have this problem, and this keeps the two in step. There the
 * rows are the listed policies themselves and each feature's findings
 * (`features/{id}/findings`) are matched to a row by proxy_id, not id — so a sibling id is
 * never looked up. Switching an imported comparison from company to feature view therefore
 * shows the listed policy; filling the column through any other id would show one wording
 * in company view and another in feature view.
 */
const toListedPolicies = (imported: IPolicy[], listed: IPolicy[]): IPolicy[] =>
  uniqBy(
    imported.map(
      (policy) =>
        listed.find((p) => p.id === policy.id) ??
        (policy.proxy_id ? listed.find((p) => p.proxy_id === policy.proxy_id) : undefined) ??
        policy,
    ),
    (policy) => policy.id,
  )

const policyLabel = (policy: IPolicy): string =>
  policy?.name || policy?.code || `Policy ${policy?.id ?? ''}`.trim()

/**
 * What to import. The bare list is the whole of it in the ordinary case; `coverIds` is
 * there for when the user was shown the wording's covers and kept only some of them, and
 * `classId` for when the wording spans classes and the user picked which to compare it in.
 */
export type ImportedPolicyLoadArg =
  | IPolicy[]
  | { policies: IPolicy[]; coverIds?: number[]; classId?: number | string }

/**
 * Narrows a resolved placement to the covers the user kept.
 *
 * Only the covers change; which class the import belongs to was decided by the wording and
 * is not the user's to edit here. Placeability is recomputed against the narrowed selection
 * so the existing rule still holds: a policy the table cannot show is named back to the
 * caller rather than given a column with no rows in it.
 */
const narrowToChosenCovers = (
  state: IComparisonState,
  imported: IPolicy[],
  placement: ImportedPolicyPlacement,
  chosen: number[],
): ImportedPolicyPlacement => {
  const kept = placement.coverIds
    .map((id, index) => ({ id, name: placement.coverTypes[index] }))
    .filter(({ id }) => chosen.includes(id))

  if (!kept.length) return placement

  const coverIds = kept.map(({ id }) => id)
  return {
    ...placement,
    coverIds,
    coverTypes: kept.map(({ name }) => name),
    ...splitPlaceablePolicies(state, imported, coverIds),
  }
}

/**
 * Puts the policies an uploaded PDF resolved to into the comparison.
 *
 * One wording can be published by several insurers, so an import can name more than one
 * policy and they all belong on screen together. They are placed as a set — one class, one
 * cover selection, one confirmation — and whatever does not fit is named back to the caller
 * so it can say so, rather than being dropped without a word.
 */
export const loadImportedPdfPolicyAsync = createAsyncThunk(
  'comparison/load-imported-pdf-policy',
  async (arg: ImportedPolicyLoadArg, { getState, dispatch }): Promise<ImportedPolicyLoadResult> => {
    const matches = Array.isArray(arg) ? arg : arg?.policies
    const chosenCoverIds = Array.isArray(arg) ? undefined : arg?.coverIds
    const chosenClassId = Array.isArray(arg) ? undefined : arg?.classId

    // Placement is decided entirely on cover ids, so it has to run against the real
    // catalogue rather than the seed productClasses starts on.
    await dispatch(ensureProductClassesAsync())

    const state = (getState() as IRootState).comparison
    // The same policy named twice in one body would otherwise take two turns at one column.
    const imported = uniqBy(
      (matches ?? []).filter((policy) => policy?.id != null),
      (policy) => policy.id,
    )

    const nothing = (
      mode: ImportedPolicyPlacementMode,
      reason: ImportedPolicyLoadResult['reason'],
      skippedForLimit: string[] = [],
      skippedOutOfClass: string[] = [],
    ): ImportedPolicyLoadResult => ({
      mode,
      added: false,
      addedNames: [],
      refreshedNames: [],
      skippedForLimit,
      skippedOutOfClass,
      alreadySelected: false,
      reason,
    })

    if (!imported.length) return nothing('append', 'no-policies')

    const resolved = resolveImportedPolicyPlacement(state, imported, chosenClassId)
    // A cover the user unticked is unticked whichever mode this is. The import never cuts
    // covers on its own — narrowToChosenCovers only ever runs on a list the user was shown
    // and edited, and only within the covers the placement had already resolved.
    const placement = chosenCoverIds?.length
      ? narrowToChosenCovers(state, imported, resolved, chosenCoverIds)
      : resolved
    const skippedOutOfClass = placement.excludedPolicies.map(policyLabel)

    // Every cover the wordings name has been retired, and there is no comparison loaded
    // to fall back on. Clearing to an empty cover selection would leave a blank table
    // and no way back, so refuse the import instead and let the caller explain why.
    if (placement.mode === 'reset' && !placement.coverIds.length) {
      return nothing(placement.mode, 'no-available-covers', [], skippedOutOfClass)
    }

    /**
     * An append normally leaves the cover selection alone — but the user can have unticked
     * one in the dialog, and then the selection has moved even though the placement did
     * not. Comparing the ids is what catches that; comparing the mode alone would drop the
     * change on the floor.
     */
    const currentCoverIds = state.selectedProductClassCover?.coverId ?? []
    const coversChanged =
      placement.mode !== 'append' ||
      placement.coverIds.length !== currentCoverIds.length ||
      placement.coverIds.some((id) => !currentCoverIds.includes(id))

    const placeableIds = new Set(placement.policies.map((p) => p.id))
    const placeable = toListedPolicies(
      imported.filter((policy) => placeableIds.has(policy.id)),
      coversChanged
        ? await fetchPolicies({ cover_id: placement.coverIds }).catch(() => [])
        : state.comparisonPolicies ?? [],
    )

    // A reset clears the comparison first, so every column is free and nothing counts as
    // already selected. Otherwise the existing comparison and its column limit stand.
    const occupied = placement.mode === 'reset' ? [] : state.selectedPolicyContents
    const occupiedIds = new Set(occupied.map((content: IFeatureContents) => content.id))
    const refreshed = placeable.filter((policy) => occupiedIds.has(policy.id))
    const fresh = placeable.filter((policy) => !occupiedIds.has(policy.id))

    const room = Math.max(0, ComparisonColumn.MAX_SELECTED_LENGTH - occupied.length)
    const toAdd = fresh.slice(0, room)
    const skippedForLimit = fresh.slice(room).map(policyLabel)

    if (!toAdd.length && !refreshed.length) {
      // Nothing landed. Which of the two reasons it was decides what the user can do about
      // it — free up a column, or open a comparison in the other class.
      const reason = skippedForLimit.length ? 'column-limit' : 'out-of-class'
      return nothing(placement.mode, reason, skippedForLimit, skippedOutOfClass)
    }

    if (placement.mode === 'reset') {
      dispatch(resetComparison())
    }

    if (coversChanged) {
      dispatch(
        selectProductClassCover({
          className: placement.className,
          classId: placement.classId,
          coverId: placement.coverIds,
          coverType: placement.coverTypes,
        }),
      )
    }

    // Kept in the order the import named them, so a refreshed column stays where it was
    // and the new ones follow in the order the response listed them.
    const acceptedIds = new Set([...refreshed, ...toAdd].map((policy) => policy.id))
    const accepted = placeable.filter((policy) => acceptedIds.has(policy.id))

    /**
     * Which column each accepted policy takes. A policy already on screen keeps the column
     * it is in, so a re-upload refreshes in place; the rest fill up from the end of the
     * comparison. The slots are worked out here rather than in the reducer because the
     * findings request is made against the same slot — two separate answers to "which
     * column is this?" is how a response lands in the wrong one.
     */
    const slotOfPolicy = new Map<number, number>()
    occupied.forEach((content: IFeatureContents, index: number) =>
      slotOfPolicy.set(content.id, content.slot ?? index),
    )
    let nextFreeSlot = occupied.length
    const placements = accepted.map((policy) => {
      const held = slotOfPolicy.get(policy.id)
      // eslint-disable-next-line no-plusplus
      return { policy, slot: held ?? nextFreeSlot++ }
    })

    // The columns go up first, headed by the policy the wording matched, so there is
    // something to read while the rest is fetched.
    dispatch(placeImportedPolicyColumns(placements))

    // An imported policy is a column, and columns are only rendered in COMPANY mode.
    // Left in FEATURE mode the import lands in state and shows nothing at all, which
    // reads to the user as the upload having done nothing.
    if (state.filter.compareBy !== CompareBy.COMPANY) {
      dispatch(setCompareBy(CompareBy.COMPANY))
    }

    /**
     * The covers' features are the comparison's rows, and a finding is only ever shown
     * against the row its feature_id names. On an empty comparison — or any import that
     * moves the cover selection — there are no rows yet, so the features are loaded and
     * waited for before the findings are asked for. Fetched the other way round, the
     * findings arrive against a table that has nowhere to put them.
     */
    if (coversChanged) {
      await Promise.all([
        dispatch(fetchFeaturesAsync(placement.coverIds)),
        dispatch(fetchPoliciesAsync({ cover_id: placement.coverIds })),
      ])
    }

    // Each column's findings are asked for separately and arrive independently, so the
    // comparison fills in column by column instead of staying blank until the slowest
    // one lands.
    placements.forEach(({ policy, slot }) => {
      dispatch(fetchPolicyContentByIdAsync({ policy, slot }))
    })
    dispatch(setImportedPdfData(null))

    return {
      mode: placement.mode,
      added: true,
      addedNames: toAdd.map(policyLabel),
      refreshedNames: refreshed.map(policyLabel),
      skippedForLimit,
      skippedOutOfClass,
      alreadySelected: !toAdd.length && refreshed.length > 0,
    }
  },
)
